import { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, ArrowLeft, Building2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError as formatApiErrorDetail } from '@/lib/utils';

const LOGO = "https://customer-assets.emergentagent.com/job_9b5e32bd-6e03-4600-841f-a63162f850e8/artifacts/w487gkes_lexorbit-logo.png.png";

export default function AuthCard3D({ onSuccess, defaultPlan }) {
  const [isFlipped, setIsFlipped] = useState(!!defaultPlan);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, register, requestPasswordReset } = useAuth();

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regOrgName, setRegOrgName] = useState('');
  const [regPlan, setRegPlan] = useState(defaultPlan || 'independiente');
  const [regTerminos, setRegTerminos] = useState(false);

  // Recuperar contraseña
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setIsLoading(true);
    try { const d = await login(loginEmail, loginPassword); if (onSuccess) onSuccess(d); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setIsLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError('');
    if (regPassword !== regConfirm) { setError('Las contraseñas no coinciden'); return; }
    if (regPassword.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    if (!regOrgName.trim()) { setError('Ingrese el nombre de su organización o estudio'); return; }
    if (!regTerminos) { setError('Debés aceptar los Términos y Condiciones y la Política de Privacidad'); return; }
    setIsLoading(true);
    try {
      const d = await register(regName, regEmail, regPassword, regOrgName, regPlan, regTerminos);
      setIsLoading(false);
      if (onSuccess) onSuccess(d);
    } catch (err) {
      setIsLoading(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || 'Error al crear la cuenta.');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault(); setError(''); setIsLoading(true);
    try {
      await requestPasswordReset(forgotEmail);
      setForgotSent(true);
    } catch {
      // El backend responde uniforme; mostramos confirmación igual
      setForgotSent(true);
    } finally { setIsLoading(false); }
  };

  const flip = () => { setError(''); setForgot(false); setForgotSent(false); setIsFlipped(!isFlipped); setShowPassword(false); };
  const inputClass = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#B8B8B8]/40 rounded-lg px-4 py-3 pl-11 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/50 transition-all duration-300";

  return (
    <div className="perspective-1200 w-full max-w-[440px] mx-auto" data-testid="auth-card-3d">
      <motion.div className="relative w-full preserve-3d" animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }} style={{ transformStyle: 'preserve-3d' }}>

        {/* LOGIN */}
        <div className="w-full backface-hidden" style={{ backfaceVisibility: 'hidden' }}>
          <div className="bg-[#0a0a0a]/95 backdrop-blur-2xl border border-[rgba(201,164,76,0.18)] rounded-2xl p-8 lg:p-9" data-testid="login-form">
            <div className="text-center mb-7">
              <img src={LOGO} alt="LexOrbit" className="h-14 mx-auto mb-4" />
              <h2 className="text-[26px] text-[#F8F6F0] tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                {forgot ? 'Recuperar acceso' : 'Iniciar Sesión'}
              </h2>
              <p className="text-[#B8B8B8] text-xs mt-1.5">
                {forgot ? 'Te enviamos un enlace para crear una nueva contraseña' : 'Accedé a tu entorno privado de gestión legal'}
              </p>
            </div>
            {error && !isFlipped && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 mb-4 text-red-400 text-xs" data-testid="login-error">{error}</div>}

            {forgot ? (
              forgotSent ? (
                <div className="text-center space-y-4">
                  <CheckCircle2 className="w-11 h-11 text-green-400 mx-auto" />
                  <p className="text-[#B8B8B8] text-sm">Si el correo está registrado, vas a recibir un enlace para restablecer tu contraseña. Revisá tu casilla (y el spam).</p>
                  <button onClick={() => { setForgot(false); setForgotSent(false); }} className="text-[#C9A44C] hover:text-[#E0C878] text-sm font-medium flex items-center gap-1 mx-auto">
                    <ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-3.5">
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                    <input type="email" placeholder="Tu correo electrónico" className={inputClass} value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required />
                  </div>
                  <button type="submit" disabled={isLoading}
                    className="w-full text-[#050505] font-bold py-3 rounded-lg flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                    {isLoading ? 'Enviando...' : 'Enviar enlace'}{!isLoading && <ArrowRight className="w-4 h-4" />}
                  </button>
                  <button type="button" onClick={() => { setForgot(false); setError(''); }} className="w-full text-[#B8B8B8]/60 hover:text-[#F8F6F0] text-xs flex items-center justify-center gap-1">
                    <ArrowLeft className="w-3.5 h-3.5" /> Volver
                  </button>
                </form>
              )
            ) : (
              <>
                <form onSubmit={handleLogin} className="space-y-3.5">
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                    <input type="email" placeholder="Correo electrónico" className={inputClass} value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required data-testid="login-email-input" />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                    <input type={showPassword ? "text" : "password"} placeholder="Contraseña" className={inputClass} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#B8B8B8]/40 hover:text-[#C9A44C] transition-colors" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="text-right">
                    <button type="button" onClick={() => { setForgot(true); setError(''); setForgotEmail(loginEmail); }}
                      className="text-[11px] text-[#C9A44C]/80 hover:text-[#E0C878] transition-colors">
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                  <button type="submit" disabled={isLoading}
                    className="w-full text-[#050505] font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm disabled:opacity-50 hover:shadow-[0_0_25px_rgba(201,164,76,0.35)]"
                    style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}
                    data-testid="login-submit-button">
                    {isLoading ? 'Ingresando...' : 'Ingresar'}{!isLoading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>
                <p className="text-center text-[#B8B8B8]/50 text-xs mt-6">¿No tiene cuenta? <button onClick={flip} className="text-[#C9A44C] hover:text-[#E0C878] transition-colors font-medium" data-testid="flip-to-register-button">Crear cuenta</button></p>
              </>
            )}
          </div>
        </div>

        {/* REGISTER */}
        <div className="w-full absolute top-0 left-0 rotate-y-180 backface-hidden" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
          <div className="bg-[#0a0a0a]/95 backdrop-blur-2xl border border-[rgba(201,164,76,0.18)] rounded-2xl p-8 lg:p-9" data-testid="register-form">
            <div className="text-center mb-6">
              <img src={LOGO} alt="LexOrbit" className="h-14 mx-auto mb-4" />
              <h2 className="text-[26px] text-[#F8F6F0] tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>Crear Cuenta</h2>
              <p className="text-[#B8B8B8] text-xs mt-1.5">Creá tu organización y probá el sistema 5 días gratis</p>
            </div>
            {error && isFlipped && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 mb-4 text-red-400 text-xs" data-testid="register-error">{error}</div>}
            <form onSubmit={handleRegister} className="space-y-3">
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type="text" placeholder="Nombre completo" className={inputClass} value={regName} onChange={e => setRegName(e.target.value)} required data-testid="register-name-input" />
              </div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type="email" placeholder="Correo electrónico" className={inputClass} value={regEmail} onChange={e => setRegEmail(e.target.value)} required data-testid="register-email-input" />
              </div>
              <div className="relative">
                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type="text" placeholder="Nombre de su estudio / organización" className={inputClass} value={regOrgName} onChange={e => setRegOrgName(e.target.value)} required data-testid="register-org-input" />
              </div>
              <select value={regPlan} onChange={e => setRegPlan(e.target.value)}
                className="w-full bg-white/[0.04] border-2 border-[rgba(201,164,76,0.25)] text-[#F8F6F0] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/50 transition-all duration-300"
                data-testid="register-plan-select">
                <option value="independiente" className="bg-[#141414]">Abogado Independiente</option>
                <option value="estudio_3" className="bg-[#141414]">Estudio hasta 3 abogados</option>
                <option value="estudio_5" className="bg-[#141414]">Estudio hasta 5 abogados</option>
                <option value="estudio_10" className="bg-[#141414]">Estudio hasta 10 abogados</option>
                <option value="custom" className="bg-[#141414]">Plan a medida</option>
              </select>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type={showPassword ? "text" : "password"} placeholder="Contraseña" className={inputClass} value={regPassword} onChange={e => setRegPassword(e.target.value)} required data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#B8B8B8]/40 hover:text-[#C9A44C] transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type="password" placeholder="Confirmar contraseña" className={inputClass} value={regConfirm} onChange={e => setRegConfirm(e.target.value)} required data-testid="register-confirm-input" />
              </div>
              <label className="flex items-start gap-2 cursor-pointer select-none pt-1">
                <input type="checkbox" checked={regTerminos} onChange={e => setRegTerminos(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#C9A44C] shrink-0" data-testid="register-terminos-checkbox" />
                <span className="text-[11px] text-[#B8B8B8] leading-relaxed">
                  Acepto los{' '}
                  <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[#C9A44C] hover:text-[#E0C878] underline">Términos y Condiciones</a>{' '}
                  y la{' '}
                  <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[#C9A44C] hover:text-[#E0C878] underline">Política de Privacidad</a>.
                </span>
              </label>
              <button type="submit" disabled={isLoading || !regTerminos}
                className="w-full text-[#050505] font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_25px_rgba(201,164,76,0.35)]"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}
                data-testid="register-submit-button">
                {isLoading ? 'Creando cuenta...' : 'Crear Cuenta y Probar 5 Días'}{!isLoading && <ArrowRight className="w-4 h-4" />}
              </button>
              <p className="text-[#B8B8B8]/40 text-[10px] text-center mt-1 leading-relaxed">Tu prueba de 5 días comienza al crear la organización. Los datos de cada estudio se mantienen separados.</p>
            </form>
            <p className="text-center text-[#B8B8B8]/50 text-xs mt-5">¿Ya tiene cuenta? <button onClick={flip} className="text-[#C9A44C] hover:text-[#E0C878] transition-colors font-medium" data-testid="flip-to-login-button">Iniciar sesión</button></p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
